import { useState } from 'react';
import { C } from '../../lib/tokens.js';

export function Button({ kind = 'default', size = 'md', icon, iconRight, children, onClick, disabled, type = 'button', title, full, style: extra }) {
  const sizes = {
    sm: { padY: 4,  padX: 10, fs: 12, gap: 5, ic: 14 },
    md: { padY: 7,  padX: 14, fs: 13, gap: 6, ic: 16 },
    lg: { padY: 10, padX: 18, fs: 14, gap: 8, ic: 18 },
  };
  const sz = sizes[size];
  const base = {
    fontFamily: 'Roboto, sans-serif', fontSize: sz.fs, fontWeight: 500,
    padding: `${sz.padY}px ${sz.padX}px`, borderRadius: 4,
    border: '1px solid transparent', cursor: disabled ? 'not-allowed' : 'pointer',
    display: full ? 'flex' : 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: sz.gap, transition: 'background 180ms cubic-bezier(.4,0,.2,1), color 180ms, border-color 180ms',
    opacity: disabled ? 0.55 : 1, whiteSpace: 'nowrap', width: full ? '100%' : undefined,
  };
  const kinds = {
    default: { background: C.silver,      color: C.fg,  borderColor: C.silver },
    primary: { background: C.liquid,      color: C.tin, borderColor: C.liquid },
    deepsea: { background: C.deepsea,     color: C.tin, borderColor: C.deepsea },
    ghost:   { background: 'transparent', color: C.liquid, borderColor: C.silver },
    danger:  { background: C.rust,        color: C.tin, borderColor: C.rust },
    subtle:  { background: 'transparent', color: C.iron, borderColor: 'transparent' },
    outline: { background: C.tin,         color: C.fg,  borderColor: C.mercury },
  };
  const hovers = {
    default: { background: C.liquid,     color: C.tin, borderColor: C.liquid },
    primary: { background: C.liquidDk,   color: C.tin, borderColor: C.liquidDk },
    deepsea: { background: '#1A2856',    color: C.tin, borderColor: '#1A2856' },
    ghost:   { background: C.liquid,     color: C.tin, borderColor: C.liquid },
    danger:  { background: '#B5061B',    color: C.tin, borderColor: '#B5061B' },
    subtle:  { background: C.rowHover,   color: C.liquid, borderColor: 'transparent' },
    outline: { background: C.platinum,   color: C.fg,  borderColor: C.silver },
  };
  const [hov, setHov] = useState(false);
  const merged = { ...base, ...kinds[kind], ...(hov && !disabled ? hovers[kind] : {}), ...extra };
  return (
    <button type={type} title={title} style={merged} onClick={onClick} disabled={disabled}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}>
      {icon ? <span className="material-icons" style={{ fontSize: sz.ic }}>{icon}</span> : null}
      {children}
      {iconRight ? <span className="material-icons" style={{ fontSize: sz.ic }}>{iconRight}</span> : null}
    </button>
  );
}

export function IconButton({ icon, onClick, title, size = 32, color = C.iron, hoverBg = C.platinum, active, disabled, progress }) {
  const [h, setH] = useState(false);
  const bg = active ? C.rowActive : (h && !disabled ? hoverBg : 'transparent');
  const showRing = progress === true || typeof progress === 'number';
  return (
    <button onClick={disabled ? undefined : onClick} title={title} type="button" disabled={disabled}
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        width: size, height: size, borderRadius: 4, border: 'none', background: bg,
        cursor: disabled ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: active ? C.liquid : color, opacity: disabled && !showRing ? 0.5 : 1, transition: 'background 120ms, color 120ms',
      }}>
      {showRing ? <ProgressRing size={Math.round(size * 0.6)} progress={typeof progress === 'number' ? progress : null} color={color} />
        : <span className="material-icons" style={{ fontSize: Math.round(size * 0.6) }}>{icon}</span>}
    </button>
  );
}

function ProgressRing({ size, progress, color }) {
  const stroke = Math.max(2, Math.round(size * 0.12));
  const r = size / 2 - stroke;
  const circumference = 2 * Math.PI * r;
  const determinate = typeof progress === 'number';
  const offset = determinate ? circumference * (1 - Math.min(Math.max(progress, 0), 1)) : circumference * 0.75;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
      style={{ animation: determinate ? 'none' : 'spin 0.9s linear infinite' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={C.mercury} strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: determinate ? 'stroke-dashoffset 120ms linear' : 'none' }} />
    </svg>
  );
}
